// @ts-nocheck
import Zone from "@insite/client-framework/Components/Zone";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import validateReCaptcha from "@insite/client-framework/Store/Components/ReCaptcha/Handlers/ValidateReCaptcha";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ReCaptcha from "@insite/content-library/Components/ReCaptcha";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import ReCAPTCHA from "react-google-recaptcha";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";
import FormActions from "@store/Library/Components/Toolkit/FormActions";
import InfosBox, { InfosBoxProps } from "@store/Library/Components/Toolkit/InfosBox";
import ga from "@toolkit/Framework/Common/GoogleAnalytics";
import React, { RefObject, useEffect, useRef, useState } from "react";
import submitWarrantyForm from "@store/Framework/Store/Store/Components/WarrantyForm/Handlers/SubmitWarrantyForm";
import clearForm from "@store/Framework/Store/Store/Components/WarrantyForm/Handlers/ClearForm";
import WarrantyFormFields from "@store/Library/Widgets/Warranty/WarrantyFormFields";
import setFieldValue from "@store/Framework/Store/Store/Components/WarrantyForm/Handlers/SetFieldValue";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import FormWrapper from "@store/Library/Components/Toolkit/FormWrapper";
import { getCurrentUserIsGuest } from "@insite/client-framework/Store/Context/ContextSelectors";

const enum fields {
    emailRecipients = "emailRecipients",
    submitButtonText = "submitButtonText",
    successMessage = "successMessage",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.emailRecipients]: string;
        [fields.submitButtonText]: string;
        [fields.successMessage]: string;
    };
}

const mapStateToProps = (state: ApplicationState) => {
    const { userName } = state.context.session;
    const currentUserIsGuest = getCurrentUserIsGuest(state);
    return {
        fieldValues: state.store.components.warrantyForm.fieldValues,
        isSignedIn: userName && !currentUserIsGuest,
    };
};
const mapDispatchToProps = {
    submitWarrantyForm,
    setFieldValue,
    clearForm,
    validateReCaptcha: makeHandlerChainAwaitable<
        { location: string; recaptchaRef: RefObject<ReCAPTCHA | null> },
        boolean
    >(validateReCaptcha),
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface WarrantyFormStyles {
    container?: GridContainerProps;
    formGridItem?: GridItemProps;
    submitButton?: ButtonPresentationProps;
    successModal?: ModalPresentationProps;
    modalContainer?: GridContainerProps;
    messageTextGridItem?: GridItemProps;
    messageText?: TypographyPresentationProps;
    buttonGridItem?: GridItemProps;
    continueButton?: ButtonPresentationProps;
    successBox?: InfosBoxProps;
    legalNoticeBox?: InfosBoxProps;
}

export const warrantyFormStyles: WarrantyFormStyles = {
    container: {
        css: css`
            form {
                width: 100%;
            }
        `,
    },
    formGridItem: { width: 12 },
    successModal: { sizeVariant: "small" },
    successBox: {
        type: "success",
    },
    legalNoticeBox: {
        type: "info",
        extendedStyles: {
            content: {
                css: css`
                    width: 100%;
                `,
            },
        },
    },
};

const styles = warrantyFormStyles;

type Validator = () => boolean;
type ContextType = {
    validators: {
        [key: string]: Validator | undefined;
    };
};

export const WarrantyFormContext = React.createContext<ContextType>({
    validators: {},
});

const WarrantyForm = ({ id, fields, submitWarrantyForm, clearForm, validateReCaptcha, isSignedIn }: Props) => {
    const { submitButtonText, successMessage } = fields;
    const [validators] = useState<{ [key: string]: Validator | undefined }>({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const recaptchaRef: RefObject<ReCAPTCHA | null> = React.createRef();

    const trackFormStart = ga.use.trackFormStart({ form_name: "warranty_form" });
    const trackFormSubmit = ga.use.trackFormSubmit({ form_name: "warranty_form" });
    const canTrackFormStartRef = useRef(true);
    const formRef = useRef<HTMLFormElement>(null);

    useEffect(() => {
        const handleFormFocus = () => {
            if (formRef.current?.contains(document.activeElement) && canTrackFormStartRef.current) {
                trackFormStart();
                canTrackFormStartRef.current = false;
            }
        };

        formRef.current?.addEventListener("focus", handleFormFocus, true);

        return () => {
            formRef.current?.removeEventListener("focus", handleFormFocus);
        };
    }, []);

    const handleFormSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const widgetValidators: Validator[] = [];
        Object.keys(validators).forEach(key => {
            const validator = validators[key];
            if (validator) {
                widgetValidators.push(validator);
            }
        });
        const results = widgetValidators.map(v => v());
        if (!results.every(o => o)) {
            return;
        }

        const isReCaptchaValid = await validateReCaptcha({ location: "WarrantyForm", recaptchaRef });

        if (!isReCaptchaValid) {
            return;
        }

        setIsSubmitting(true);
        submitWarrantyForm({
            onSuccess: () => {
                setIsModalOpen(true);
                setIsSubmitting(false);
                trackFormSubmit();
                canTrackFormStartRef.current = true;
            },
            onComplete() {
                // "this" is targeting the object being created, not the parent SFC

                this.onSuccess?.();
            },
        });
    };

    const modalCloseHandler = () => {
        clearForm();
        setIsModalOpen(false);
        window.location.reload();
    };

    if (!isSignedIn) {
        return <>{translate("Only available for signed in users. Please sign in.")}</>;
    }

    return (
        <FormWrapper noFocusNoScroll>
            <GridContainer {...styles.container}>
                <GridItem {...styles.formGridItem}>
                    <WarrantyFormContext.Provider value={{ validators }}>
                        <form id="warrantyForm" onSubmit={handleFormSubmit} noValidate ref={formRef}>
                            <Zone contentId={id} zoneName="Content" />
                            <WarrantyFormFields />
                            <ReCaptcha recaptchaRef={recaptchaRef} location="WarrantyForm" />
                            <FormActions>
                                <Button {...styles.submitButton} type="submit" disabled={isSubmitting}>
                                    {submitButtonText}
                                </Button>
                            </FormActions>
                            <br />
                            <InfosBox {...styles.legalNoticeBox}>
                                <strong>{translate("General Warranty Notice")}</strong>
                                <br />
                                <em>
                                    {translate(
                                        "DCNE administers the manufacturer's warranty. Please read specific warranty card that is provided equipment for the equipment coverage. ",
                                    )}
                                </em>
                                <br />
                                <strong>
                                    {translate("All claims must be submitted within 30 days of repair.")}
                                    <br />
                                    {translate(
                                        "Failed components must be held by dealer for 30 days and turned in to DCNE upon request.",
                                    )}
                                    <br />
                                    {translate(
                                        "All replacement items claimed must be sourced from DCNE. Freight is not a covered expense.",
                                    )}
                                </strong>
                            </InfosBox>
                            <Modal
                                headline={translate("Message Sent")}
                                contentLabel={successMessage}
                                {...styles.successModal}
                                isOpen={isModalOpen}
                                handleClose={modalCloseHandler}
                            >
                                <InfosBox {...styles.successBox}>{successMessage}</InfosBox>
                                <FormActions>
                                    <Button {...styles.continueButton} onClick={modalCloseHandler}>
                                        {translate("Continue")}
                                    </Button>
                                </FormActions>
                            </Modal>
                        </form>
                    </WarrantyFormContext.Provider>
                </GridItem>
            </GridContainer>
        </FormWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(WarrantyForm),
    definition: {
        group: "Contact Us",
        icon: "input-pipe",
        fieldDefinitions: [
            {
                name: fields.submitButtonText,
                displayName: "Submit Button Text",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Send",
            },
            {
                name: fields.successMessage,
                displayName: "Submit Success Message",
                editorTemplate: "RichTextField",
                fieldType: "Translatable",
                defaultValue: "Thank you for your inquiry. A customer service representative will contact you shortly.",
            },
        ],
    },
};

export default widgetModule;
